import { useState, useRef, useEffect } from 'react';
import { getWithExpiry, setWithExpiry } from '../utils/sessionStorage';
import { isUserSafari } from '../utils/safariUtils';
import { isUserFirefox } from '../utils/firefoxUtils';
import { logScreenerCameraError } from '../utils/statsig';
import { getDeviceType } from '../utils/getDeviceType';
import { getBrowser } from '../utils/getBrowser';

export const useCamera = (props?: {
  clearRecordingError?: () => void;
  hasRecorded?: boolean;
 } ) => {
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deviceRemoved, setDeviceRemoved] = useState(false);
  const [deviceReconnected, setDeviceReconnected] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  // Re-enabled - Track selected device from session storage to detect disconnections
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(
    getWithExpiry('selectedCameraDeviceId') || ''
  );

  // Get available camera devices
  const getVideoDevices = async () => {
    try {
      setError(null);
      try {
        if (isUserSafari() || isUserFirefox()){
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (e: any){
        console.error("Firefox? ", isUserFirefox(), "error: ", e);
        if(!isUserFirefox()){
          setError('Could not access camera. Please check your browser permissions and try again.');
        }
        // await navigator.mediaDevices.getUserMedia({ video: true });
      }
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(device => device.kind === 'videoinput');
      setDevices(videoDevices);
      return videoDevices;
    } catch (err) {
      console.error("GetVideoDevices error: ", err);
      setError('Could not access camera devices. Please check your browser permissions and try again.');
      setDevices([]);
      return [];
    }
  };

  // Effect: update selected device if devices change
  // useEffect(() => {
  //   const checkDevices = async () => {
  //     if (devices.length > 0) {
  //       const storedDeviceId = getWithExpiry('selectedCameraDeviceId');
  //       // recheck and fetch devices
  //       // console.warn("rechecking devices")
  //       const newDevices = await getVideoDevices();
  //       const deviceExists = newDevices.some(device => device.deviceId === storedDeviceId);
  //       // console.log("device Exists? ", deviceExists);
  //       // if (!storedDeviceId || !deviceExists) {
  //       //   const defaultDeviceId = devices[0].deviceId;
  //       //   setSelectedDeviceId(defaultDeviceId);
  //       //   setWithExpiry('selectedCameraDeviceId', defaultDeviceId, 24 * 60 * 60 * 1000);
  //       // }
  //     }
  //   };
  
  //   checkDevices();
  // }, [devices]);

  // COMMENTED OUT - No longer loading devices on mount or monitoring device changes
  // Device access will be requested when startCamera is called
  // useEffect(() => {
  //   console.log("Effect: load devices and listen for device changes...")
  //   getVideoDevices();
  //   const handleDeviceChange = async () => {
  //     const videoDevices = await getVideoDevices();
  //     const selectedExists = selectedDeviceId && videoDevices.some(d => d.deviceId === selectedDeviceId);

  //     // If camera was removed
  //     if (!selectedExists && isCameraOn && selectedDeviceId) {
  //       setDeviceRemoved(true);
  //       setDeviceReconnected(false);
  //       setError('Your camera device has been disconnected. Please reconnect your camera or select a different device.');
  //       stopCamera();
  //       console.warn("Camera was removed");
  //     }
  //     // If camera was reconnected (device now exists, camera is off, and not loading)
  //     else if (selectedExists && !isCameraOn && !isLoading) {
  //       setDeviceRemoved(false);
  //       setDeviceReconnected(true);
  //       setError(null);
  //       console.warn("Setting recording error to null...");
  //       if (props?.clearRecordingError) props.clearRecordingError();
  //       if(!props?.hasRecorded){
  //         console.log("Effect: auto-restarting camera");
  //         await startCamera(selectedDeviceId);
  //       }
  //     }
  //   };
  //   navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
  //   return () => {
  //     navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
  //   };
  //   // eslint-disable-next-line
  // }, [selectedDeviceId, isCameraOn, isLoading, deviceRemoved, deviceReconnected]);

  // Effect: set video srcObject when camera is on
  useEffect(() => {
    if (isCameraOn && streamRef.current && videoRef.current) {
      console.log('📹 useCamera: Setting video srcObject');
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isCameraOn, streamRef.current]);

  // Monitor media stream tracks for unexpected endings
  useEffect(() => {
    console.warn("Effect: handleTrackEnded, checking for device changes...")
    if (streamRef.current && isCameraOn) {
      const videoTracks = streamRef.current.getVideoTracks();
  
      const handleTrackEnded = async (event: Event) => {
        const track = event.target as MediaStreamTrack;
        if (track.kind === "video") {
          // Device was disconnected - just set error and turn camera off
          console.error("Your camera device has been disconnected. Please reconnect your camera and try again.");
          setDeviceRemoved(true);
          setDeviceReconnected(false);
          setError('Your camera device has been disconnected. Please reconnect your camera and try again.');
          setIsCameraOn(false);
          // try {
          //   await navigator.mediaDevices.getUserMedia({
          //     video: { deviceId: { exact: selectedDeviceId } }
          //   });
          //   // If this succeeds, device is still present (maybe permission issue)
          //   setIsCameraOn(false);
          //   setError('Camera stream ended, possibly due to permission change from microphone. Please check your microphone permissions.');
          //   console.error("Camera stream ended, possibly due to permission change from microphone. Please check your microphone permissions.");
          // } catch (err) {
          //   // If this fails, device is truly gone
          //   console.error("Your camera device has been disconnected. Please reconnect your camera and try again.", err);
          //   setDeviceRemoved(true);
          //   setDeviceReconnected(false);
          //   setError('Your camera device has been disconnected. Please reconnect your camera and try again.');
          //   setIsCameraOn(false);
          // }
        }
      };
  
      videoTracks.forEach(track => {
        track.addEventListener('ended', handleTrackEnded);
      });
  
      return () => {
        videoTracks.forEach(track => {
          track.removeEventListener('ended', handleTrackEnded);
        });
      };
    }
  }, [isCameraOn]);

  const startCamera = async (deviceId?: string, onboarding = false) => {
    try {
      // UPDATED - No longer using persisted device ID from session storage
      // Use provided deviceId or default to browser's default camera
      console.warn("Camera Device to use: ", deviceId || 'browser default');
      setError(null);
      setIsLoading(true);
      setDeviceRemoved(false);
      setDeviceReconnected(false);
      // // if onboarding use default device since user will choose the device
      // if(onboarding){
      //   console.log("Onboarding: Using default devices")
      //   const fetchDevices = await getVideoDevices();
      //   deviceId = fetchDevices[0].deviceId;
      //   setWithExpiry('selectedCameraDeviceId', deviceId, 24 * 60 * 60 * 1000);
      // }
      
      const isAndroid = /android/i.test(navigator.userAgent);

      // Simplified constraints - use deviceId if provided, otherwise use browser default
      const constraints = {
        video: isAndroid ? 
        {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 }
        } : deviceId
          ? { deviceId: { exact: deviceId } }
          : {
            width: { ideal: 640 },
            height: { ideal: 480 },
            frameRate: { ideal: 30 }
          },
      };
      if (isUserFirefox()) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Get the actual device ID from the stream and store it
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        const actualDeviceId = settings.deviceId;
        if (actualDeviceId) {
          console.log('📷 Storing actual camera device ID:', actualDeviceId);
          setSelectedDeviceId(actualDeviceId);
          // Store in session storage for persistence across re-recordings
          setWithExpiry('selectedCameraDeviceId', actualDeviceId, 24 * 60 * 60 * 1000); // 24 hours
        }
      }
      
      streamRef.current = stream;
      setIsCameraOn(true);
      if (videoRef.current) {
        console.log('📹 startCamera: Setting video srcObject');
        videoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err: unknown) {
      console.error("Error starting camera", err);

      const cameraError = err as { name?: string; message?: string } | null;
      logScreenerCameraError({
        jobId: getWithExpiry('jobId') || '',
        companyId: getWithExpiry('companyId') || '',
        applicantGuid: getWithExpiry('applicantGuid') || undefined,
        deviceType: getDeviceType(),
        browser: getBrowser(),
        errorCode: cameraError?.name || 'CameraStartError',
        errorMessage: cameraError?.message || String(err),
      });

      if(!isUserFirefox()){
        setError('Could not access camera. Please reconnect your device and try again.');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const stopCamera = () => {
    setError(null);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) stopCamera();
    };
    // eslint-disable-next-line
  }, []);

  // Check if the selected camera device is still available
  // Uses getUserMedia as the real verification (not just enumerateDevices which can have stale IDs)
  const checkSelectedDeviceAvailable = async (): Promise<{ available: boolean; deviceId: string | null }> => {
    try {
      const storedDeviceId = getWithExpiry('selectedCameraDeviceId');
      
      // If no device was previously selected, return true (will use default)
      if (!storedDeviceId) {
        console.log('📷 No specific camera device selected, will use browser default');
        return { available: true, deviceId: null };
      }

      // Verify by actually trying to access the device with getUserMedia
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: storedDeviceId } }
        });
        testStream.getTracks().forEach(track => track.stop());
        console.log('📷 Selected camera device verified via getUserMedia:', storedDeviceId);
        return { available: true, deviceId: storedDeviceId };
      } catch (getUserMediaError: any) {
        if (getUserMediaError?.name === 'OverconstrainedError') {
          // Device ID is stale - refresh with default device
          console.warn('📷 Stored camera ID is stale (OverconstrainedError), refreshing...');
          try {
            const freshStream = await navigator.mediaDevices.getUserMedia({ video: true });
            const freshTrack = freshStream.getVideoTracks()[0];
            if (freshTrack) {
              const freshId = freshTrack.getSettings().deviceId;
              if (freshId) {
                console.log('📷 Updated camera device ID to:', freshId);
                setSelectedDeviceId(freshId);
                setWithExpiry('selectedCameraDeviceId', freshId, 24 * 60 * 60 * 1000);
              }
            }
            freshStream.getTracks().forEach(track => track.stop());
            return { available: true, deviceId: null };
          } catch {
            // Refresh also failed - defer to permission check which will show the correct message
            console.log('📷 Camera refresh failed, deferring to permission check');
            return { available: true, deviceId: storedDeviceId };
          }
        } else {
          // NotAllowedError or any other error - defer to permission check step
          // This ensures consistent "blocked" messaging instead of "device not available"
          console.log('📷 Camera device check error, deferring to permission check:', getUserMediaError?.name);
          return { available: true, deviceId: storedDeviceId };
        }
      }
    } catch (err) {
      console.error('Error checking camera device availability:', err);
      return { available: false, deviceId: null };
    }
  };

  return {
    isCameraOn,
    isLoading,
    error,
    videoRef,
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    startCamera,
    stopCamera,
    getVideoDevices,
    deviceRemoved,
    deviceReconnected,
    setDeviceRemoved,
    setDeviceReconnected,
    stream: streamRef.current,
    checkSelectedDeviceAvailable,
  };
};

export default useCamera;